import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerPanel } from '../ui/DrawerPanel';
import { ALL_LAYOUTS, LayoutConfig, getLayoutById, getLayoutImage } from '../../constants/layouts';
import { LayoutNo911Dialog } from '../features/LayoutNo911Dialog';
import { updateConfigurationLayout } from '../../lib/supabase';
import { API_BASE_URL } from '../../config/apiUrl';
import { syncSeniorConfig } from '../../lib/syncConfig';

interface SettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  currentLayoutId?: string;
  onLayoutUpdated?: (newLayoutId: string) => void;
}

export const SettingsDrawer: FC<SettingsDrawerProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
  currentLayoutId,
  onLayoutUpdated,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedLayoutId, setSelectedLayoutId] = useState<string>(currentLayoutId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // D19 — the no-911 layout awaiting confirmation. Kept out of
  // `selectedLayoutId` so backing out leaves the caregiver on the layout the
  // senior's phone is actually running.
  const [pendingNo911, setPendingNo911] = useState<LayoutConfig | null>(null);

  // Reset selection when drawer opens with new data
  useEffect(() => {
    if (isOpen && currentLayoutId) {
      setSelectedLayoutId(currentLayoutId);
      setError(null);
      // Re-arm the warning on every open: this drawer deploys to a live phone,
      // so each switch to a no-911 layout is its own decision.
      setPendingNo911(null);
    }
  }, [isOpen, currentLayoutId]);

  /**
   * D19 gate on the dashboard side. Two things differ from the wizard:
   *
   *  - This drawer offers ALL seven layouts, not the two or three the
   *    assessment recommends, so a no-911 layout is always one tap away.
   *  - It writes to a phone an elderly person is already carrying. The wizard
   *    is a setup decision; this is a change to a device in service.
   *
   * The gate is on SELECTION rather than on Save because the selection is what
   * the caregiver reasons about — and because the Save button is disabled until
   * the selection actually changes, so gating Save would let them stare at a
   * chosen no-911 layout with no warning at all.
   */
  const handleSelect = (layout: LayoutConfig) => {
    if (!layout.has911 && layout.id !== currentLayoutId) {
      setPendingNo911(layout);
      return;
    }
    setSelectedLayoutId(layout.id);
  };

  const handleSave = async () => {
    if (!selectedLayoutId || selectedLayoutId === currentLayoutId) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await updateConfigurationLayout(seniorId, selectedLayoutId);

      // Trigger real-time sync to Android device via server
      // Auth + ownership now required server-side; syncSeniorConfig attaches
      // the JWT and never throws (see lib/syncConfig.ts).
      await syncSeniorConfig(seniorId);

      onLayoutUpdated?.(selectedLayoutId);
      onClose();
    } catch (err) {
      console.error('Failed to update layout:', err);
      setError(t('drawers.settings.errorUpdateLayout'));
    } finally {
      setSaving(false);
    }
  };

  const layoutEntries = Object.values(ALL_LAYOUTS);
  const currentLayout = currentLayoutId ? getLayoutById(currentLayoutId) : null;

  return (
    <DrawerPanel
      isOpen={isOpen}
      onClose={onClose}
      title={t('drawers.settings.title', { name: seniorName })}
      footer={
        <button
          onClick={handleSave}
          disabled={saving || !selectedLayoutId || selectedLayoutId === currentLayoutId}
          className="w-full py-3 bg-brand-primary text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
              {t('drawers.settings.saving')}
            </>
          ) : (
            t('drawers.settings.saveAndDeploy')
          )}
        </button>
      }
    >
      {currentLayout && (
        <p className="text-xs text-brand-text-muted mb-3">
          {t('drawers.settings.currentLabel')} <span className="font-semibold text-brand-text">{currentLayout.name}</span>
        </p>
      )}

      <section>
        <h3 className="text-sm font-semibold text-brand-text mb-3">{t('drawers.settings.phoneLayout')}</h3>
        <div className="grid grid-cols-2 gap-3">
          {layoutEntries.map((layout) => (
            <LayoutRow
              key={layout.id}
              layout={layout}
              isSelected={selectedLayoutId === layout.id}
              isCurrent={currentLayoutId === layout.id}
              onSelect={() => handleSelect(layout)}
              locale={i18n.language}
            />
          ))}
        </div>
      </section>

      {/* D8 (2026-08-10) — the "Call protection" section moved OUT of this
          panel into its own ScamBlockingDrawer behind its own tile. It was the
          only safety switch on the dashboard and it sat below a layout picker.
          Nothing was dropped: the toggle, the unknown-state retry and the
          "blocking is off" warning all moved verbatim. */}

      {error && (
        <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-700 flex items-center gap-2">
            <span className="material-symbols-outlined text-lg">error</span>
            {error}
          </p>
        </div>
      )}

      <p className="text-xs text-brand-text-muted mt-6">
        {t('drawers.settings.deployNote', { name: seniorName })}
      </p>

      {/* D19 — no-911 layout confirm */}
      {pendingNo911 && (
        <LayoutNo911Dialog
          layout={pendingNo911}
          onChooseAnother={() => setPendingNo911(null)}
          onContinue={() => {
            setSelectedLayoutId(pendingNo911.id);
            setPendingNo911(null);
          }}
        />
      )}
    </DrawerPanel>
  );
};

interface LayoutRowProps {
  locale: string;
  layout: LayoutConfig;
  isSelected: boolean;
  isCurrent: boolean;
  onSelect: () => void;
}

const LayoutRow: FC<LayoutRowProps> = ({ layout, isSelected, isCurrent, onSelect, locale }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex flex-col gap-2 p-2.5 rounded-xl border-2 transition-all text-left ${
        isSelected
          ? 'border-brand-primary bg-brand-primary/5'
          : 'border-brand-border hover:border-brand-primary/40'
      }`}
    >
      {/* D9 — the real Figma screenshot, replacing a generic smartphone glyph.
          Same asset set the signup wizard's picker uses, locale-aware. */}
      <div className="relative w-full flex justify-center bg-brand-bg-warm rounded-lg py-2">
        <img
          src={getLayoutImage(layout, locale)}
          alt={layout.name}
          width={412}
          height={910}
          className="h-28 w-auto object-contain rounded"
          loading="lazy"
        />
        {isSelected && (
          <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center">
            <span className="material-symbols-outlined text-white" style={{ fontSize: '14px' }}>
              check
            </span>
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <h4 className="font-semibold text-xs text-brand-text truncate">{layout.name}</h4>
          {isCurrent && (
            <span className="text-[9px] font-bold uppercase tracking-wide text-brand-primary flex-shrink-0">
              {t('drawers.settings.currentBadge')}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export default SettingsDrawer;
